/**
 * FocalZoomControl — the NON-DESTRUCTIVE crop control for an Image / GIF Segment
 * fill (EW-003 u1b, flag REACT_APP_SEGMENT_ARC_FILLS).
 *
 * Nothing is ever baked into a new asset: the user picks a FOCAL point (0..1) and
 * a ZOOM (1..4) and a display MODE (cover | contain | tile); those scalars are
 * stored on the fill model and applied at render time by BOTH surfaces
 * (toWedgePatternProps for the closed wedge, toFrameStyle for the open frame), so
 * the wedge and the frame stay in sync from ONE definition.
 *
 * Controlled + presentational: owns no server state. Parent passes { src, focus,
 * mode } and an onChange({ focus, mode }); the parent maps it onto the segment
 * fill (SegmentFillPanel → SegmentStylingPane → updateSegmentSetting).
 *
 * A11y: the focal picker is keyboard-driven (arrow keys nudge the point, Shift =
 * bigger step, Home/End jump), announces its position via aria-valuetext, has a
 * visible focus ring, and honours reduced-motion (the live preview never animates
 * here — it is a still crop preview). Zoom is a native range input (fully
 * accessible). Mode is a real ARIA radiogroup. WCAG AA contrast on all labels.
 *
 * Pure geometry is delegated to logic/focalCrop.js (unit-tested in isolation).
 */

import React from "react";
import { tokens } from "../../../../theme/tokens";
import { pointFromClientXY, previewObjectPosition } from "./logic/focalCrop";

const INK = "#123051";
const SUB = "#565556";
const LINE = "#e1e1e1";
const PL = "#9B5AF8";

const MODES = [
  { id: "cover", label: "Cover", hint: "Fill the Segment, cropping overflow" },
  { id: "contain", label: "Fit", hint: "Show the whole image, letterboxed" },
  { id: "tile", label: "Tile", hint: "Repeat the image edge-to-edge" },
];

const S = {
  wrap: { display: "flex", flexDirection: "column", gap: tokens.spacing.sm, marginTop: tokens.spacing.md },
  label: { fontSize: 12, fontWeight: tokens.fontWeight.bold, color: INK, margin: 0 },
  hint: { fontSize: 11, color: SUB, margin: 0 },
  modeRow: { display: "flex", gap: tokens.spacing.xs, flexWrap: "wrap" },
  modeBtn: (active) => ({
    flex: "1 1 auto",
    minHeight: 36,
    border: `1px solid ${active ? PL : LINE}`,
    background: active ? "#F1E9FE" : "#FFFFFF",
    color: active ? "#6f27d6" : SUB,
    borderRadius: tokens.radius.md,
    padding: "8px 10px",
    font: `600 12px ${tokens.fontFamily}`,
    cursor: "pointer",
  }),
  stage: (disabled) => ({
    position: "relative",
    width: "100%",
    maxWidth: 220,
    aspectRatio: "1 / 1",
    borderRadius: tokens.radius.md,
    border: `1px solid ${LINE}`,
    overflow: "hidden",
    outline: "none",
    cursor: disabled ? "default" : "crosshair",
    touchAction: "none",
    background: "#EDE7FB",
  }),
  dot: (x, y) => ({
    position: "absolute",
    left: `${x * 100}%`,
    top: `${y * 100}%`,
    width: 18,
    height: 18,
    marginLeft: -9,
    marginTop: -9,
    borderRadius: "50%",
    border: "2px solid #FFFFFF",
    boxShadow: "0 0 0 2px rgba(18,48,81,0.55)",
    background: "rgba(155,90,248,0.6)",
    pointerEvents: "none",
  }),
  zoomRow: { display: "flex", alignItems: "center", gap: tokens.spacing.sm },
  zoomVal: { fontSize: 11, color: SUB, minWidth: 34, textAlign: "right", fontVariantNumeric: "tabular-nums" },
  range: { flex: 1, accentColor: PL, minHeight: 24 },
};

const clamp01 = (n) => Math.min(1, Math.max(0, n));
const round2 = (n) => Math.round(n * 100) / 100;

const FocalZoomControl = ({ src, focus, mode = "cover", onChange = () => {} }) => {
  const f = focus || {};
  const x = clamp01(Number(f.x ?? 0.5));
  const y = clamp01(Number(f.y ?? 0.5));
  const zoom = Math.min(4, Math.max(1, Number(f.zoom) || 1));
  const stageRef = React.useRef(null);
  const draggingRef = React.useRef(false);

  const emitFocus = (nx, ny, nz = zoom) =>
    onChange({ focus: { x: clamp01(nx), y: clamp01(ny), zoom: nz }, mode });

  const focusFromEvent = (evt) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = pointFromClientXY(evt.clientX, evt.clientY, rect);
    emitFocus(p.x, p.y);
  };

  const onPointerDown = (evt) => {
    if (!src) return;
    draggingRef.current = true;
    evt.currentTarget.setPointerCapture?.(evt.pointerId);
    focusFromEvent(evt);
  };
  const onPointerMove = (evt) => {
    if (!draggingRef.current) return;
    focusFromEvent(evt);
  };
  const onPointerUp = (evt) => {
    draggingRef.current = false;
    evt.currentTarget.releasePointerCapture?.(evt.pointerId);
  };

  const onKeyDown = (evt) => {
    if (!src) return;
    const step = evt.shiftKey ? 0.1 : 0.02;
    let handled = true;
    switch (evt.key) {
      case "ArrowLeft": emitFocus(x - step, y); break;
      case "ArrowRight": emitFocus(x + step, y); break;
      case "ArrowUp": emitFocus(x, y - step); break;
      case "ArrowDown": emitFocus(x, y + step); break;
      case "Home": emitFocus(0.5, 0.5); break;
      default: handled = false;
    }
    if (handled) evt.preventDefault();
  };

  const onZoom = (evt) => emitFocus(x, y, Math.min(4, Math.max(1, Number(evt.target.value))));

  const objectPosition = previewObjectPosition(x, y);
  const disabled = !src;
  const posText = `Focal point ${Math.round(x * 100)}% across, ${Math.round(y * 100)}% down`;

  return (
    <div style={S.wrap} data-focal-zoom>
      {/* MODE selector (radiogroup) */}
      <p style={S.label} id="fz-mode-label">Display</p>
      <div style={S.modeRow} role="radiogroup" aria-labelledby="fz-mode-label">
        {MODES.map((m) => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={active}
              title={m.hint}
              style={S.modeBtn(active)}
              onClick={() => onChange({ focus: { x, y, zoom }, mode: m.id })}
              data-fill-mode={m.id}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      {/* FOCAL point picker (hidden for tile — a tiled fill has no focal crop) */}
      {mode !== "tile" && (
        <>
          <p style={S.label} id="fz-focal-label">Focal point</p>
          <p style={S.hint}>Drag the dot, or use arrow keys, to choose what stays centred.</p>
          <div
            ref={stageRef}
            style={S.stage(disabled)}
            role="slider"
            tabIndex={disabled ? -1 : 0}
            aria-labelledby="fz-focal-label"
            aria-valuetext={posText}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(x * 100)}
            aria-disabled={disabled ? "true" : undefined}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onKeyDown={onKeyDown}
            onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 3px ${PL}`; }}
            onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; }}
            data-focal-stage
          >
            {src && (
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: mode === "contain" ? "contain" : "cover",
                  objectPosition,
                  transform: zoom > 1 ? `scale(${zoom})` : undefined,
                  transformOrigin: objectPosition,
                  pointerEvents: "none",
                }}
              />
            )}
            {src && <span style={S.dot(x, y)} aria-hidden="true" data-focal-dot />}
          </div>
        </>
      )}

      {/* ZOOM (native range = fully accessible) */}
      {mode === "cover" && (
        <div style={S.zoomRow}>
          <label htmlFor="fz-zoom" style={S.label}>Zoom</label>
          <input
            id="fz-zoom"
            type="range"
            min={1}
            max={4}
            step={0.1}
            value={zoom}
            onChange={onZoom}
            disabled={disabled}
            style={S.range}
            aria-label="Zoom"
            data-fill-zoom
          />
          <span style={S.zoomVal} aria-hidden="true">{round2(zoom)}×</span>
        </div>
      )}
    </div>
  );
};

export default FocalZoomControl;
